import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useMigrationEntityJsonQuery, useEditMigrationJsonMutation } from './hooks/useMigrationQuery'

/**
 * Port of `app/migration/migration-json.html` +
 * `app/migration/migrationJsonController.js` (AngularJS
 * `migrationJsonController`) — the JSON viewer/editor for one migration
 * entity, reached at `/workspace/migration/json/:entity` (legacy state
 * `workspace.migration-json`, URL `/workspace/migration/json/{entity}`; see
 * `AppRoutes.jsx`).
 *
 * Legacy behavior ported 1:1:
 * - `$stateParams.entity` is the *entire* entity object, JSON-stringified by
 *   the previous screen (`MigrationDetailPage.jsx`'s `handleViewJson`) ->
 *   `JSON.parse(useParams().entity)` here (React Router already decodes the
 *   percent-encoding the previous screen added).
 * - `data-ng-init="initView()"` fetches the raw JSON text for
 *   `entity.migrationEntityId` unconditionally on mount
 *   (`MigrationService.getMigrationEntityJson`) ->
 *   `useMigrationEntityJsonQuery(entity.migrationEntityId)`.
 * - The fetched text is written verbatim into the textarea
 *   (`$('#jsonTextArea').val(response.data)`, no parsing) -> seeded into
 *   local state (`jsonText`) once the query resolves, so the field stays a
 *   normal MUI controlled `TextField` the user can then edit freely
 *   (matching the legacy plain, unvalidated `<textarea>`). Rather than a
 *   `useEffect` syncing `jsonQuery.data` into `jsonText` (React's "you might
 *   not need an effect" anti-pattern — causes an extra render and trips
 *   `react-hooks/set-state-in-effect`), this follows the same "adjust state
 *   during render" pattern already established by
 *   `client/src/modules/register/SettingsPage.jsx` (task 013): track which
 *   fetched value the local state was last derived from, and reset it
 *   whenever a *different* value arrives, while still letting the textarea
 *   freely override it in between.
 * - Heading "Json do id: {{entity.migrationEntityId}}" and the `<pre>` above
 *   the table showing `entity.statusReport`.
 * - "Voltar"/"Cancelar" (`ng-click="back()"`, not defined in this
 *   controller either — see `MigrationDetailPage.jsx`'s note on the same
 *   pattern) -> `navigate(-1)`.
 * - "Salvar" (`save()`): posts the current textarea value plus
 *   `entity.migrationEntityId` (`MigrationService.editJson`), then shows a
 *   toast and navigates to the listing screen
 *   (`$state.go("workspace.migration")` -> `/workspace/migration`) on
 *   success. Ported via `useEditMigrationJsonMutation`.
 * - No JSON syntax highlighting/validation existed in the legacy screen
 *   (plain Materialize `<textarea>`, raw string in/out) — a plain MUI
 *   `TextField multiline` is used here rather than pulling in a new
 *   dependency (e.g. `@monaco-editor/react`/`react-json-view`) for a
 *   textarea that never validated or highlighted anything to begin with;
 *   this matches the "no new dependency without an existing behavior to
 *   justify it" reasoning already used in `MigrationListPage.jsx` (`Table`
 *   vs. `DataGrid`).
 *
 * Not ported: the `#fountainG`-style Materialize chrome and the
 * `.trigger('autoresize')` jQuery plugin call — the MUI `TextField`'s own
 * `multiline`/`minRows` handle textarea sizing, no jQuery plugin needed.
 */
function MigrationJsonPage() {
  const { entity: entityParam } = useParams()
  const navigate = useNavigate()

  const entity = JSON.parse(entityParam)

  const jsonQuery = useMigrationEntityJsonQuery(entity.migrationEntityId)
  const editMutation = useEditMigrationJsonMutation()

  const [jsonText, setJsonText] = useState('')
  const [lastSeenJson, setLastSeenJson] = useState(undefined)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  if (jsonQuery.data != null && jsonQuery.data !== lastSeenJson) {
    setLastSeenJson(jsonQuery.data)
    setJsonText(jsonQuery.data)
  }

  function handleBack() {
    navigate(-1)
  }

  /** Port of `save()`. */
  function handleSave() {
    editMutation.mutate(
      { json: jsonText, migrationEntityId: entity.migrationEntityId },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Json enviado para edição. Verifique se o erro foi corrigido na lista de migração!',
          })
          navigate('/workspace/migration')
        },
        onError: () => {
          setSnackbar({ open: true, message: 'Erro ao salvar o Json.' })
        },
      },
    )
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  const isLoading = jsonQuery.isPending

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Json do id: {entity.migrationEntityId}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Link component="button" type="button" onClick={handleBack}>
          Voltar
        </Link>
      </Box>

      {entity.statusReport && (
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
          {entity.statusReport}
        </Box>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {jsonQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar o Json desta entidade.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <TextField
          id="jsonTextArea"
          label="Dados"
          value={jsonText}
          onChange={(event) => setJsonText(event.target.value)}
          multiline
          minRows={10}
          fullWidth
        />
      </Paper>

      <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleBack} disabled={editMutation.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={editMutation.isPending}>
          Salvar
        </Button>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MigrationJsonPage
